import { useState } from "react";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import { TextAlign } from "@tiptap/extension-text-align";
import { FONTS, useFonts } from "../../FdDesignSetting/premiumDesign";
import { usePagePro } from "../../FdSubscription/proPlan";
import { blocksToDoc, docToBlocks, spanStyle, TEXT_COLORS, TEXT_SIZES } from "../postContent";

const TOOL = "flex h-9 min-w-9 items-center justify-center rounded-lg px-2.5 text-sm font-semibold transition";
const SELECT = "h-9 rounded-lg bg-transparent px-1.5 text-sm font-semibold text-gray-700 outline-none hover:bg-white focus-visible:ring-2 focus-visible:ring-gray-300";

// 글자 꾸미기는 정해진 값(크기·색·폰트 키)으로 저장하고 화면에는 style 로 그린다
const styleAttr = (name) => ({
    default: null,
    parseHTML: () => null, // 붙여 넣기는 글자만이라 HTML 에서 읽지 않는다
    renderHTML: (attrs) => {
        const css = Object.entries(spanStyle({ [name]: attrs[name] })).map(([k, v]) => `${k.replace(/[A-Z]/g, c => `-${c.toLowerCase()}`)}: ${v}`).join('; ');
        return css ? { style: css } : {};
    },
});
const PostTextStyle = TextStyle.extend({
    addAttributes: () => ({ size: styleAttr('size'), color: styleAttr('color'), font: styleAttr('font') }),
});

// 서버 텍스트 블록이 담을 수 있는 것만: 문단 · 제목(h2) · 굵게 · 글자 꾸미기 · 정렬 · 줄바꿈 (사진은 이미지 모듈로)
const EXTENSIONS = [
    StarterKit.configure({
        heading: { levels: [2] },
        blockquote: false, bulletList: false, orderedList: false, listItem: false, listKeymap: false,
        code: false, codeBlock: false, horizontalRule: false,
        italic: false, strike: false, underline: false, link: false,
    }),
    PostTextStyle,
    TextAlign.configure({ types: ['heading', 'paragraph'], alignments: ['left', 'center', 'right'] }),
];

const ALIGN_TOOLS = [
    { value: 'left', label: '왼쪽 정렬' },
    { value: 'center', label: '가운데 정렬' },
    { value: 'right', label: '오른쪽 정렬' },
];

function AlignIcon({ align }) {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
            {[16, 10, 14].map((w, i) => {
                const x = align === 'center' ? (16 - w) / 2 : align === 'right' ? 16 - w : 0;
                return <rect key={i} x={x} y={2 + i * 5} width={w} height="2" rx="1" fill="currentColor" />;
            })}
        </svg>
    );
}

const keepFocus = (e) => e.preventDefault(); // 도구를 눌러도 편집기 커서가 사라지지 않게

/** 텍스트 모듈: Tiptap + 제목·굵게 도구. 바뀔 때마다 onChange(블록 배열) */
export function FgPostTextModule({ blocks, onChange, placeholder }) {
    useFonts();
    const isPro = usePagePro();
    const [colorOpen, setColorOpen] = useState(false);
    // 처음 한 번만 넣는다 (모듈마다 key 가 고정이라 다시 만들어지지 않는다)
    const editor = useEditor({
        extensions: EXTENSIONS,
        content: blocksToDoc(blocks),
        onUpdate: ({ editor }) => onChange(docToBlocks(editor.getJSON())),
        editorProps: {
            attributes: {
                role: 'textbox',
                'aria-multiline': 'true',
                'aria-label': '본문',
                class: "min-h-[120px] break-words text-[16px] leading-[1.85] text-gray-800 outline-none [&_h2]:mb-1 [&_h2]:mt-6 [&_h2]:text-[21px] [&_h2]:font-bold [&_h2]:leading-snug [&_h2]:text-gray-900",
            },
            // 붙여 넣기는 글자만 (다른 사이트의 서식·이미지가 섞이지 않게). pasteText 는 clipboardData 없는 이벤트로 다시 들어온다
            handlePaste: (view, event) => {
                if (!event.clipboardData?.types.includes('text/html')) {
                    return false;
                }
                view.pasteText(event.clipboardData.getData('text/plain'));
                return true;
            },
            handleDrop: (view, event, slice, moved) => !moved, // 밖에서 끌어 온 HTML·파일은 받지 않는다 (편집기 안 이동은 허용)
        },
    });

    // 커서 위치의 굵게·제목 상태를 도구 버튼에 표시
    const toolState = useEditorState({
        editor,
        selector: ({ editor }) => {
            const style = editor.getAttributes('textStyle');
            return {
                bold: editor.isActive('bold'),
                heading: editor.isActive('heading'),
                size: style.size || null,
                color: style.color || null,
                font: style.font || null,
                align: ALIGN_TOOLS.find(a => editor.isActive({ textAlign: a.value }))?.value || 'left',
                isEmpty: editor.isEmpty,
            };
        },
    });

    // 고른 글자(없으면 커서 뒤에 쓸 글자)에 꾸미기. null 은 빼기
    const setStyle = (attrs) => editor.chain().focus().setMark('textStyle', attrs).removeEmptyTextStyle().run();

    return (
        <div className="flex flex-col gap-2">
            <div className="sticky top-0 z-10 flex flex-col gap-1 rounded-xl bg-gray-50 p-1 ring-1 ring-black/[0.04]" role="toolbar" aria-label="본문 도구">
                <div className="flex flex-wrap items-center gap-1">
                    <button type="button" onMouseDown={keepFocus} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} aria-pressed={toolState.heading} title="제목 (한 번 더 누르면 본문)" className={`${TOOL} ${toolState.heading ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-white'}`}>
                        제목
                    </button>
                    <button type="button" onMouseDown={keepFocus} onClick={() => editor.chain().focus().toggleBold().run()} aria-pressed={toolState.bold} title="굵게 (Ctrl+B)" className={`${TOOL} font-extrabold ${toolState.bold ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-white'}`}>
                        B
                    </button>
                    <span className="mx-0.5 h-5 w-px bg-gray-200" aria-hidden="true" />
                    <select value={toolState.size || ''} onChange={(e) => setStyle({ size: e.target.value || null })} aria-label="글자 크기" className={SELECT}>
                        {TEXT_SIZES.map(s => <option key={s.label} value={s.value || ''}>{s.label}</option>)}
                    </select>
                    <select value={toolState.font || ''} onChange={(e) => setStyle({ font: e.target.value || null })} aria-label="글꼴" className={`${SELECT} max-w-[110px]`}>
                        <option value="">기본 글꼴</option>
                        {FONTS.map(f => (
                            // Pro 가 아니면 새 폰트는 못 고른다 (이미 쓴 폰트는 그대로 둘 수 있음 — 서버도 같은 규칙)
                            <option key={f.value} value={f.value} disabled={!isPro && f.value !== toolState.font}>{f.label}{isPro ? '' : ' (Pro)'}</option>
                        ))}
                    </select>
                    <button type="button" onMouseDown={keepFocus} onClick={() => setColorOpen(open => !open)} aria-expanded={colorOpen} aria-label="글자 색" title="글자 색" className={`${TOOL} flex-col gap-0 text-gray-700 ${colorOpen ? 'bg-white' : 'hover:bg-white'}`}>
                        <span className="leading-none">A</span>
                        <span className="mt-0.5 h-[3px] w-4 rounded-full" style={{ background: toolState.color || '#1f2937' }} aria-hidden="true" />
                    </button>
                    <span className="mx-0.5 h-5 w-px bg-gray-200" aria-hidden="true" />
                    {ALIGN_TOOLS.map(a => (
                        <button key={a.value} type="button" onMouseDown={keepFocus} onClick={() => editor.chain().focus().setTextAlign(a.value).run()} aria-pressed={toolState.align === a.value} aria-label={a.label} title={a.label} className={`${TOOL} px-2 ${toolState.align === a.value ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-white'}`}>
                            <AlignIcon align={a.value} />
                        </button>
                    ))}
                </div>
                {colorOpen &&
                    <div className="flex flex-wrap items-center gap-2 px-1.5 pb-1 pt-0.5" aria-label="글자 색 고르기">
                        <button type="button" onMouseDown={keepFocus} onClick={() => setStyle({ color: null })} aria-pressed={!toolState.color} className={`rounded-full px-3 py-1 text-[12px] font-semibold ring-1 ${!toolState.color ? 'bg-gray-900 text-white ring-gray-900' : 'bg-white text-gray-700 ring-gray-200'}`}>
                            기본
                        </button>
                        {TEXT_COLORS.map(c => (
                            <button
                                key={c}
                                type="button"
                                onMouseDown={keepFocus}
                                onClick={() => setStyle({ color: c })}
                                aria-label={`색상 ${c}`}
                                aria-pressed={toolState.color === c}
                                className={`h-7 w-7 rounded-full ring-1 ring-black/10 ${toolState.color === c ? 'outline outline-2 outline-offset-2 outline-gray-900' : ''}`}
                                style={{ background: c }}
                            />
                        ))}
                        <label className="relative flex h-7 w-7 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-[conic-gradient(red,yellow,lime,cyan,blue,magenta,red)] ring-1 ring-black/10" title="직접 고르기">
                            <input type="color" value={toolState.color || '#111111'} onChange={(e) => setStyle({ color: e.target.value.toUpperCase() })} className="absolute inset-0 cursor-pointer opacity-0" aria-label="색상 직접 고르기" />
                        </label>
                    </div>
                }
            </div>
            <div className="relative">
                {toolState.isEmpty &&
                    <p className="pointer-events-none absolute left-0 top-0 text-[16px] leading-[1.85] text-gray-300" aria-hidden="true">{placeholder}</p>
                }
                <EditorContent editor={editor} />
            </div>
        </div>
    );
}
